import { CartApi } from '@theme/cart-api';
import { DealSync, normalizeNestedPlans } from '@theme/deal-sync';

// Clicks on +/-/frequency are coalesced per line for this long before a sync starts.
const INTENT_DEBOUNCE_MS = 200;

// pubsub-v2.js and cart-constants-v2.js are deferred scripts that run before this module. When one
// failed to load (seen in in-app browsers), install the same minimal versions so the drawer, and the
// components publishing to it, keep working instead of throwing on first use.
function ensurePubSub() {
  const missing = [];
  if (!window.PUB_SUB_EVENTS) {
    missing.push('PUB_SUB_EVENTS');
    window.PUB_SUB_EVENTS = { cartUpdate: 'cart-update', cartError: 'cart-error' };
  }
  if (typeof window.subscribe !== 'function' || typeof window.publish !== 'function') {
    missing.push('pubsub');
    const subscribers = {};
    window.subscribe = (eventName, callback) => {
      subscribers[eventName] = [...(subscribers[eventName] || []), callback];
      return () => {
        subscribers[eventName] = subscribers[eventName].filter((cb) => cb !== callback);
      };
    };
    window.publish = (eventName, data) =>
      Promise.all((subscribers[eventName] || []).map((callback) => callback(data)));
  }
  if (missing.length) {
    window.captureThemeError?.(new Error('Cart pubsub fallback installed'), 'cart-drawer-v2', { missing }, { level: 'warning' });
  }
}

ensurePubSub();

class CartDrawerV2 extends HTMLElement {
  #intents = new Map();
  #keyAlias = new Map();
  #flushTimer = null;
  // Cart operations run one at a time through this chain, so none is dropped.
  #queue = Promise.resolve();
  #queued = 0;
  #refusedGifts = new Set();

  constructor() {
    super();
    this._busy = false;
    // Cart template: main-cart runs this engine inline, so there is nothing to open or close,
    // and the page re-renders from its own section (cart state still comes from the drawer's).
    this.isPage = this.hasAttribute('data-page-mode');
    this.viewSection = this.dataset.renderSection || 'cart-drawer-v2';
    this.addEventListener('keyup', (event) => event.code === 'Escape' && this.close());
    this.querySelector('[data-cart-drawer-v2-overlay]')?.addEventListener('click', () => this.close());
    this.querySelector('[data-cart-drawer-v2-close]')?.addEventListener('click', () => this.close());
    this.addEventListener('cart-line:change', this.#onLineChange);
    this.addEventListener('click', this.#onCheckoutGuard, true);
    this.addEventListener('keydown', this.#onCheckoutGuard, true);
    if (!this.isPage) this.bindHeaderTriggers();
  }

  connectedCallback() {
    this.cartUpdateUnsubscriber = window.subscribe(window.PUB_SUB_EVENTS.cartUpdate, (event) => {
      if (event?.source !== 'cart-drawer-v2') this.onCartUpdate(event);
    });
    // Hook for apps that change the cart without publishing cartUpdate.
    window.refreshCart = () => this.open({ refresh: true });
    // BOGOS free-gift app, per theme setting (cart page excluded: BOGOS reloads it itself).
    if (this.hasAttribute('data-bogos-sync')) document.addEventListener('fg-cart:auto-updated', this.#onBogosGiftChange);
    if (this.isPage) window.addEventListener('pageshow', this.#onPageShow);
  }

  disconnectedCallback() {
    this.cartUpdateUnsubscriber?.();
    document.removeEventListener('fg-cart:auto-updated', this.#onBogosGiftChange);
    window.removeEventListener('pageshow', this.#onPageShow);
  }

  // BOGOS adds/removes its gifts with its own cart writes after the drawer has rendered, so
  // re-read the cart once queued operations finish (pending clicks render fresher state anyway).
  #onBogosGiftChange = () => {
    void this.#enqueue(async () => {
      try {
        const state = await CartApi.fetchState();
        if (!this.#intents.size) this.renderContents(state);
      } catch (error) {
        window.captureThemeError?.(error, 'cart-drawer-v2', { action: 'bogos-gift-refresh', code: error?.code || null });
      }
    });
  };

  // A page restored from the back/forward cache shows the cart as it was when the shopper left.
  #onPageShow = (event) => {
    if (event.persisted) this.#refreshOnOpen();
  };

  bindHeaderTriggers() {
    const selector =
      '[data-header-v2-cart-trigger], [aria-controls="site-cart-sidebar"], a[href="/cart"], a[href="/cart/"]';
    document.addEventListener(
      'click',
      (event) => {
        const trigger = event.target.closest(selector);
        if (!trigger || document.body.classList.contains('template-cart')) return;
        event.preventDefault();
        event.stopPropagation();
        this.open({ refresh: true });
      },
      { capture: true },
    );
  }

  // refresh: re-read the cart, which may have changed in another tab, through an app that
  // doesn't publish cartUpdate, or since a back/forward-cache restore.
  open({ refresh = false } = {}) {
    if (!this.isPage) {
      this.setAttribute('open', '');
      document.body.classList.add('cart-drawer-v2-open');
    }
    if (refresh) this.#refreshOnOpen();
  }

  #refreshOnOpen() {
    if (this.#queued > 0 || this.hasAttribute('data-syncing')) return;
    void this.#enqueue(async () => {
      try {
        const state = await CartApi.fetchState();
        // A click made while the read was in flight is applied from fresher state.
        if (this.#intents.size) return;
        this.hideError();
        this.renderContents(state);
        window.publish(window.PUB_SUB_EVENTS.cartUpdate, { source: 'cart-drawer-v2', cartData: state });
      } catch (error) {
        window.captureThemeError?.(error, 'cart-drawer-v2', { action: 'refresh-on-open', code: error?.code || null });
      }
    });
  }

  close() {
    if (this.isPage) return;
    this.removeAttribute('open');
    document.body.classList.remove('cart-drawer-v2-open');
  }

  onCartUpdate(event) {
    if (event?.source === 'cart-drawer-v2') return;
    if (event?.source === 'cart-gift-v2') {
      if (event.cartData) {
        this.hideError();
        this.renderContents(event.cartData);
      }
      return;
    }
    if (!event?.cartData) return;
    // Event data is stale once an earlier queued operation has run, so refetch instead.
    const stale = this.#queued > 0;
    return this.#enqueue(() =>
      this.#runControlledOperation({
        openDrawer: !event.silent,
        focus: null,
        reloadRecommendations: event.source === 'cart-recommendations',
        mutate: async () => {
          const state = stale ? await CartApi.fetchState() : await this.#hydrateState(event.cartData);
          return normalizeNestedPlans(CartApi, state);
        },
      }),
    );
  }

  #enqueue(operation) {
    this.#queued += 1;
    const run = this.#queue.then(operation, operation).finally(() => {
      this.#queued -= 1;
    });
    this.#queue = run.catch(() => {});
    return run;
  }

  // Section responses must hold the cart state (drawer) and this view's own section.
  #hasView(sections) {
    return Boolean(sections?.['cart-drawer-v2'] && sections?.[this.viewSection]);
  }

  renderContents(parsedState, { reloadRecommendations = false } = {}) {
    // CartState already parsed the drawer HTML (state.doc); only raw section payloads need parsing.
    const viewHtml = parsedState.sections?.[this.viewSection];
    const viewDoc =
      (this.viewSection === 'cart-drawer-v2' && parsedState.doc) ||
      (viewHtml ? new DOMParser().parseFromString(viewHtml, 'text/html') : null);

    this.updateEmptyState(parsedState, viewDoc);
    if (viewDoc) {
      this.carryOverRecommendations(viewDoc, reloadRecommendations);
      this.replaceInner('[data-cart-drawer-body]', viewDoc);
      this.replaceInner('[data-cart-drawer-footer]', viewDoc);
    }

    const headerSources = ['header', 'header-v2']
      .map((id) => parsedState.sections?.[id])
      .filter(Boolean)
      .map((html) => new DOMParser().parseFromString(html, 'text/html'));
    this.syncHeaderText('[data-header-cart-count]', headerSources);
    this.syncHeaderText('[data-header-cart-total]', headerSources);
    if (this.getAttribute('aria-busy') === 'true') this.#lockDrawerControls();
  }

  carryOverRecommendations(drawerDoc, reload = false) {
    const next = drawerDoc.querySelector('cart-recommendations-v2');
    const live = this.querySelector('cart-recommendations-v2');
    if (!next || next.children.length || !live?.children.length) return;

    const clone = live.cloneNode(true);
    clone.querySelectorAll('.is-loading').forEach((el) => {
      el.classList.remove('is-loading');
      el.removeAttribute('aria-busy');
      if (el.hasAttribute('data-cart-drawer-loading-lock')) return;
      if ('disabled' in el) el.disabled = false;
      el.removeAttribute('disabled');
    });
    next.innerHTML = clone.innerHTML;
    if (reload || next.dataset.productId !== live.dataset.productId) {
      next.dataset.crReload = 'true';
    }
  }

  updateEmptyState(parsedState, viewDoc) {
    const emptyClass = this.isPage ? 'cart-section--empty' : 'cart-is-empty';
    if (typeof parsedState.item_count === 'number') {
      this.classList.toggle(emptyClass, parsedState.item_count === 0);
      return;
    }
    const root = viewDoc?.querySelector('cart-drawer-v2');
    if (root) this.classList.toggle(emptyClass, root.classList.contains(emptyClass));
  }

  syncHeaderText(selector, sourceDocs) {
    for (const doc of sourceDocs) {
      const next = doc.querySelector(selector);
      if (!next) continue;
      document.querySelectorAll(selector).forEach((node) => {
        node.textContent = next.textContent;
      });
      return;
    }
  }

  replaceInner(selector, sourceDoc) {
    const target = this.querySelector(selector);
    const source = sourceDoc.querySelector(selector);
    if (target && source) target.innerHTML = source.innerHTML;
  }

  #onCheckoutGuard = (event) => {
    const checkout = event.target.closest('[data-cart-drawer-checkout]');
    if (!checkout || !this.contains(checkout)) return;
    // data-syncing also covers the debounce window, before any operation has started.
    const settling = this._busy || this.hasAttribute('data-syncing');
    if (!settling && checkout.getAttribute('aria-disabled') !== 'true') return;
    if (event.type === 'keydown' && event.key !== 'Enter' && event.key !== ' ') return;
    event.preventDefault();
    event.stopPropagation();
  };

  #onLineChange = (event) => {
    const detail = event.detail;
    if (!detail?.lineKey) return;
    event.stopPropagation();
    this.updateLine(detail);
  };

  // Cart state from the rendered drawer's snapshot (no network); null if it can't be read.
  #liveState() {
    try {
      return CartApi.parseState({ 'cart-drawer-v2': this.outerHTML });
    } catch {
      return null;
    }
  }

  // Line intents are queued per line (latest wins) and flushed one operation at a time, so rapid
  // clicks while a sync is in flight are merged instead of dropped.
  updateLine(detail = {}) {
    if (!detail.lineKey) return Promise.resolve(false);
    // A deal parent is tracked by its offer group id: its line key changes with every plan or
    // tier change, so a click made after a change would otherwise look like a different line.
    const groupId = detail.hasDeal ? detail.line?.dataset?.groupId : null;
    const queueKey = groupId ? `group:${groupId}` : detail.lineKey;
    const prev = this.#intents.get(queueKey);
    const next = { ...prev };
    // Merge only what this action sets, so "+1" keeps a pending frequency change and vice versa.
    for (const [field, value] of Object.entries(detail)) {
      if (value !== undefined) next[field] = value;
    }
    // Paid extras of a locked gift accumulate across rapid clicks instead of the latest winning.
    if (detail.addQuantity) next.addQuantity = (prev?.addQuantity || 0) + detail.addQuantity;
    next.groupId = groupId || null;
    next.restore = prev?.restore || detail.restore;
    this.#intents.set(queueKey, next);
    this.#markIntent(detail, groupId);
    // White veil over every card from the first click until the whole queue is applied.
    this.setAttribute('data-syncing', '');
    clearTimeout(this.#flushTimer);
    this.#flushTimer = setTimeout(() => void this.#enqueue(() => this.#flushIntents()), INTENT_DEBOUNCE_MS);
    return Promise.resolve(true);
  }

  // ---- shimmer: show which parts of the drawer are being recalculated ----
  #markSync(line, tokens) {
    if (!line) return;
    const current = new Set((line.dataset.sync || '').split(' ').filter(Boolean));
    tokens.forEach((token) => current.add(token));
    line.dataset.sync = [...current].join(' ');
    line.classList.add('is-syncing');
    line.closest('li')?.classList.add('is-shimmer');
  }

  // Instant feedback at click time: a frequency change will also re-plan the gifts of that offer.
  #markIntent(detail, groupId) {
    const line = detail.line || this.getLineItem(detail.lineKey);
    if (detail.sellingPlan !== undefined) {
      this.#markSync(line, ['plan', 'price']);
      if (groupId) {
        this.querySelectorAll(`cart-line-v2[data-group-id="${CSS.escape(groupId)}"]:not([data-has-deal])`).forEach(
          (gift) => this.#markSync(gift, ['plan']),
        );
      }
    } else if (detail.quantity !== undefined) {
      this.#markSync(line, ['price']);
    }
  }

  // From DealSync once the diff is known: precise lines, plus a skeleton row for gifts being added.
  #markAffected({ parentKey, changeKeys = [], addCount = 0 }) {
    const parent = this.getLineItem(parentKey);
    this.#markSync(parent, ['price']);
    changeKeys.forEach((key) => this.#markSync(this.getLineItem(key), ['plan']));
    if (!addCount || !parent) return;
    const groupId = parent.dataset.groupId;
    const group = groupId
      ? [...this.querySelectorAll(`cart-line-v2[data-group-id="${CSS.escape(groupId)}"]`)]
      : [parent];
    const anchor = (group[group.length - 1] || parent).closest('li');
    if (!anchor || anchor.nextElementSibling?.classList.contains('line-item-v2-skeleton')) return;
    for (let i = 0; i < addCount; i += 1) {
      const row = document.createElement('li');
      row.className = 'cart-drawer-v2__item line-item-v2-skeleton';
      row.setAttribute('aria-hidden', 'true');
      row.innerHTML = '<span class="line-item-v2-skeleton__media"></span><span class="line-item-v2-skeleton__lines"><span></span><span></span><span></span></span>';
      anchor.after(row);
    }
  }

  #clearSync() {
    this.querySelectorAll('.is-syncing').forEach((el) => {
      el.classList.remove('is-syncing');
      delete el.dataset.sync;
    });
    this.querySelectorAll('.is-shimmer').forEach((el) => el.classList.remove('is-shimmer'));
    this.querySelectorAll('.line-item-v2-skeleton').forEach((el) => el.remove());
  }

  // The deal parent line currently showing for an offer group (its key may have changed).
  #parentLineForGroup(groupId) {
    return this.querySelector(
      `cart-line-v2[data-group-id="${CSS.escape(groupId)}"][data-has-deal="true"]`,
    );
  }

  async #flushIntents() {
    try {
      while (this.#intents.size) {
        // Everything queued so far is applied as one operation (one loading state, one render).
        const batch = [...this.#intents.values()].map((intent) => {
          const parentLine = intent.groupId ? this.#parentLineForGroup(intent.groupId) : null;
          const lineKey = parentLine?.dataset.lineKey || this.#keyAlias.get(intent.lineKey) || intent.lineKey;
          return { ...intent, lineKey, line: parentLine || this.getLineItem(lineKey) || intent.line };
        });
        this.#intents.clear();
        await this.#applyBatch(batch);
      }
    } finally {
      this.#keyAlias.clear();
      if (!this.#intents.size) this.removeAttribute('data-syncing');
    }
  }

  // Clicks made while a sync was running target the freshly rendered lines: show them as pending.
  #reapplyPendingQuantities() {
    for (const intent of this.#intents.values()) {
      const line = intent.groupId
        ? this.#parentLineForGroup(intent.groupId)
        : this.getLineItem(this.#keyAlias.get(intent.lineKey) || intent.lineKey);
      if (intent.quantity != null) line?.setPendingQuantity?.(intent.quantity);
      this.#markIntent({ ...intent, line }, intent.groupId);
    }
  }

  #applyBatch(batch) {
    const [first] = batch;
    return this.#runControlledOperation({
      lineKey: first.lineKey,
      button: first.button,
      focus: first.focus,
      lineEl: first.line,
      batch,
      mutate: async () => {
        const gift = document.querySelector('cart-gift-v2');
        if (gift?.whenIdle) await gift.whenIdle();

        // Cart writes to different parents are NOT safe to run concurrently (Shopify can drop
        // some of them), so parents run one after another, each from the freshest cart state.
        let result = null;
        let failure = null;
        for (const intent of batch) {
          try {
            result = await this.#mutateLine({ ...intent, state: result?.state });
            const key = result?.parentKey;
            if (key && key !== intent.lineKey) this.#keyAlias.set(intent.lineKey, key);
          } catch (error) {
            failure = failure || error;
          }
        }
        if (!result) throw failure;
        if (failure && !(failure.isCartApi && failure.status === 422)) {
          window.captureThemeError?.(failure, 'cart-drawer-v2', { action: 'batch' });
        }
        // result belongs to the LAST intent; report the first line's own new key (for focus).
        return { ...result, parentKey: this.#keyAlias.get(first.lineKey) || null, failure };
      },
    });
  }

  // Offers the customer did not touch must still hold exactly the gifts their tier requires.
  // Cart writes can, rarely, disturb a neighbouring offer (nested lines are re-linked by Shopify),
  // so after every sync each other deal parent is checked (no network) and healed only if it drifted.
  async #auditOtherOffers(result) {
    let current = result;
    const checked = new Set([result.parentKey]);
    for (const parent of result.state.items.filter((item) => item.isDealParent && !item.parentKey)) {
      if (checked.has(parent.key)) continue;
      checked.add(parent.key);
      const live = current.state.line(parent.key);
      if (!live) continue;
      const options = { api: CartApi, state: current.state, parentKey: parent.key, refusedGifts: this.#refusedGifts };
      const drift = new DealSync(options).workSummary();
      if (!drift) continue;
      // Healed just below; a failed heal is reported as an error.
      window.captureThemeError?.(
        new Error('Offer drifted while another offer was synced'),
        'cart-drawer-v2',
        { action: 'audit-heal', group: live.groupId, syncedParent: result.parentKey, drift },
        { level: 'warning' },
      );
      try {
        current = await new DealSync(options).run();
        current = { ...current, parentKey: result.parentKey };
      } catch (error) {
        window.captureThemeError?.(error, 'cart-drawer-v2', { action: 'audit-heal-failed' });
      }
    }
    return current;
  }

  async #mutateLine({ lineKey, quantity, sellingPlan, addVariantId, addQuantity, line, hasDeal, state: freshState } = {}) {
    if (addVariantId && addQuantity > 0) {
      const items = [{ id: Number.parseInt(addVariantId, 10), quantity: addQuantity }];
      return { state: await this.#write('add', { items }), mutated: true };
    }

    const live = this.getLineItem(lineKey) || line;
    let state = freshState;
    let isDeal = hasDeal || live?.dataset?.hasDeal === 'true';
    if (quantity !== 0 && !isDeal) {
      state = state || this.#liveState();
      isDeal = Boolean(state?.hasDealLine(lineKey));
    }
    if (quantity !== 0 && isDeal) {
      state = state || this.#liveState() || (await CartApi.fetchState());
      const result = await new DealSync({
        api: CartApi,
        state,
        parentKey: lineKey,
        quantity,
        sellingPlan,
        refusedGifts: this.#refusedGifts,
        onHideKeys: (keys) => {
          for (const key of keys) {
            const el = this.getLineItem(key);
            el?.markRemoving?.();
            el?.closest('li')?.classList.add('is-shimmer');
          }
        },
        onAffected: (info) => this.#markAffected(info),
      }).run();
      return this.#auditOtherOffers(result);
    }

    // By key: with a stale DOM an index can point at another line.
    const payload = { id: lineKey, quantity };
    if (sellingPlan !== undefined) {
      payload.selling_plan = sellingPlan === null || sellingPlan === '' ? null : Number(sellingPlan);
    }
    const next = await this.#write('change', payload);
    return { state: next, mutated: true, parentKey: this.#rekeyedLine(next, lineKey, live, sellingPlan) };
  }

  // One cart write whose response carries the drawer sections (refetched only if they're missing).
  async #write(action, payload) {
    const data = await CartApi.request(action, payload, { sections: true });
    return this.#hasView(data?.sections) ? CartApi.stateFromSections(data.sections) : CartApi.fetchState();
  }

  // A selling plan change re-keys a plain line. Find its new key so clicks queued against the
  // old one still apply; only an unambiguous match counts.
  #rekeyedLine(state, lineKey, live, sellingPlan) {
    if (sellingPlan === undefined || state.line(lineKey)) return null;
    const variantId = live?.dataset?.variantId;
    if (!variantId) return null;
    const plan = sellingPlan === null || sellingPlan === '' ? null : String(sellingPlan);
    const matches = state.items.filter(
      (item) =>
        !item.parentKey &&
        String(item.variantId) === variantId &&
        (item.sellingPlanId == null ? null : String(item.sellingPlanId)) === plan,
    );
    return matches.length === 1 ? matches[0].key : null;
  }

  async #hydrateState(cartData) {
    if (this.#hasView(cartData?.sections)) {
      return CartApi.stateFromSections(cartData.sections);
    }
    return CartApi.fetchState();
  }

  #setBusy(isBusy) {
    this._busy = isBusy;
    if (isBusy) document.documentElement.setAttribute('data-cart-drawer-busy', '');
    else document.documentElement.removeAttribute('data-cart-drawer-busy');
  }

  #setDrawerLoading(isLoading) {
    if (isLoading) {
      this.setAttribute('aria-busy', 'true');
      this.#lockDrawerControls();
      return;
    }
    this.removeAttribute('aria-busy');
    this.querySelectorAll('[data-cart-drawer-loading-lock]').forEach((el) => {
      el.removeAttribute('data-cart-drawer-loading-lock');
      if (el.matches('[data-cart-drawer-checkout]')) el.removeAttribute('aria-disabled');
      else el.disabled = false;
    });
  }

  #lockDrawerControls() {
    this.querySelectorAll('button, input, select').forEach((el) => {
      if (el.disabled || el.closest('[data-line-control]') || el.matches('[data-line-control]')) return;
      el.setAttribute('data-cart-drawer-loading-lock', '');
      el.disabled = true;
    });
    this.querySelectorAll('[data-cart-drawer-checkout]').forEach((el) => {
      if (el.getAttribute('aria-disabled') === 'true') return;
      el.setAttribute('data-cart-drawer-loading-lock', '');
      el.setAttribute('aria-disabled', 'true');
    });
  }

  #restoreFocus(target) {
    if (!target) return;
    let line = target.lineKey ? this.getLineItem(target.lineKey) : null;
    if (!line && target.variantId) {
      const matches = this.querySelectorAll(
        `cart-line-v2[data-variant-id="${CSS.escape(target.variantId)}"]`,
      );
      if (matches.length === 1) line = matches[0];
    }
    if (!line) return this.querySelector('[data-cart-drawer-v2-close]')?.focus?.();
    const preferred = target.type
      ? line.querySelector(`[data-line-control="${target.type}"]:not([disabled])`)
      : null;
    const fallback = line.querySelector(
      'button:not([disabled]), select:not([disabled]), input:not([disabled])',
    );
    (preferred || fallback || this.querySelector('[data-cart-drawer-v2-close]'))?.focus?.();
  }

  #shopperErrorMessage(error) {
    if (typeof error?.shopperMessage === 'string' && error.shopperMessage) return error.shopperMessage;
    // A 5xx has no shopper-facing text: its message is our own "Cart change failed" fallback.
    if (error?.isCartApi && !(error.status >= 500) && typeof error.message === 'string' && error.message) {
      return error.message;
    }
    return this.dataset.errorGeneric || 'Something went wrong. Please refresh the page.';
  }

  async #finalizeCartState(cart, renderOptions) {
    const state = this.#hasView(cart?.sections) ? cart : await CartApi.fetchState();
    this.hideError();
    this.renderContents(state, renderOptions);
    return state;
  }

  async #runControlledOperation({ lineKey, button, focus, openDrawer = false, lineEl, batch = [], reloadRecommendations = false, mutate }) {
    this.#setBusy(true);
    this.hideError();
    this.#setDrawerLoading(true);
    if (openDrawer) this.open();
    const loadingLine = lineEl || (lineKey ? this.getLineItem(lineKey) : null);
    loadingLine?.enableLoading?.({ button });

    let finalCart = null;
    let syncedParentKey = null;
    try {
      const result = await mutate();
      const state = result?.state ?? result;
      syncedParentKey = result?.parentKey || null;
      finalCart = await this.#finalizeCartState(state, { reloadRecommendations });
      this.#reapplyPendingQuantities();
      // Part of a multi-line batch failed: the rest applied, but the shopper must see why.
      if (result?.failure) {
        this.showError(this.#shopperErrorMessage(result.failure));
        return false;
      }
      return true;
    } catch (error) {
      // Some writes may have landed: show the real cart, and roll back only if that refetch fails.
      let recoveryError = null;
      try {
        const recovered = await CartApi.fetchState();
        this.renderContents(recovered);
        finalCart = recovered;
        this.#reapplyPendingQuantities();
        // Line removed elsewhere (e.g. another tab): the refreshed cart is the answer.
        if (error?.isCartApi && error.status === 400 && lineKey && !recovered.line(lineKey)) return false;
      } catch (err) {
        recoveryError = err;
        for (const intent of batch) {
          const line = this.getLineItem(intent.lineKey) || intent.line;
          line?.rollback?.(intent.restore);
        }
      }
      // A failed read that the refetch fixed leaves nothing for the shopper to act on.
      const readRecovered = !recoveryError && /^SECTIONS_/.test(error?.code || '');
      if (!readRecovered) this.showError(this.#shopperErrorMessage(error));
      if (!(error?.isCartApi && error?.status === 422)) {
        window.captureThemeError?.(error, 'cart-drawer-v2', {
          action: 'controlled',
          code: error?.code || null,
          status: error?.status || null,
          recoveryError: recoveryError?.message || null,
          recoveryCode: recoveryError?.code || null,
        });
      }
      return false;
    } finally {
      const liveLine = (lineKey && this.getLineItem(lineKey)) || lineEl;
      liveLine?.disableLoading?.();
      this.#clearSync();
      this.#setDrawerLoading(false);
      this.#setBusy(false);
      if (finalCart) window.publish(window.PUB_SUB_EVENTS.cartUpdate, { source: 'cart-drawer-v2', cartData: finalCart });
      if (focus && syncedParentKey) focus = { ...focus, lineKey: syncedParentKey };
      this.#restoreFocus(focus);
    }
  }

  getLineItem(line) {
    return this.querySelector(`cart-line-v2[data-line-key="${CSS.escape(line)}"]`);
  }

  showError(message) {
    const slot = this.querySelector('[data-cart-drawer-error]');
    if (!slot) return;
    slot.textContent = message || this.dataset.errorGeneric || 'Something went wrong. Please refresh the page.';
    slot.removeAttribute('hidden');
  }

  hideError() {
    const slot = this.querySelector('[data-cart-drawer-error]');
    if (!slot) return;
    slot.textContent = '';
    slot.setAttribute('hidden', '');
  }
}

if (!customElements.get('cart-drawer-v2')) customElements.define('cart-drawer-v2', CartDrawerV2);
